import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../base-query';

export type HomeCardGroup =
  | 'CRM'
  | 'Lists and search'
  | 'Research and writing'
  | 'Outreach'
  | 'Agents and team';

export interface HomeCardRow {
  name?: string;
  title?: string;
  company?: string;
  domain?: string;
  email?: string;
  status?: string;
  count?: number;
}

export interface HomeCardLogo {
  name?: string;
  domain?: string;
}

export interface HomeCardPreview {
  kind: 'rows' | 'logos' | 'items';
  rows?: HomeCardRow[];
  logos?: HomeCardLogo[];
  items?: string[];
}

export interface HomeCard {
  id: string;
  group: HomeCardGroup | string;
  title: string;
  prompt: string;
  locked: boolean;
  /** What the plan leaves out of a locked card: the first of its needs the
   *  plan doesn't allow. */
  locked_by?: 'crm' | 'agents' | 'sequences' | 'search' | 'plan';
  metric?: { value: number; total?: number; cached?: number };
  preview: HomeCardPreview;
}

export interface HomeCardsResponse {
  cards: HomeCard[];
  refreshed_at: string;
}

/** Who the cards are for: the set is the member's own, in one org. */
export interface HomeCardsMember {
  orgId: string;
  userId: string;
}

const expectOrg = (orgId: string) => ({ 'X-Cleanlist-Expected-Org': orgId });

/**
 * How old, in seconds, a set may get: the counts and prompts follow the
 * org's lists and CRM, which other pages change. A view mounted or returned
 * to reads the set again once it is older (`refetchOnMountOrArgChange`), and
 * `useHomeCards` polls on the same clock while one stays open.
 */
export const HOME_CARDS_FRESH_S = 5 * 60;

/**
 * The cards under Clu's composer on Home and in a new chat: cleanse-api
 * `/api/v1/home/cards`. Cached per org and member (`userId` only keys the
 * cache). A dismiss and a click read the set again afterwards, so the
 * server's refill takes the empty slot; `useHomeCards` hides a dismissed card
 * while that read is out.
 */
export const homeCardsApi = createApi({
  reducerPath: 'homeCardsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['HomeCards'],
  refetchOnMountOrArgChange: HOME_CARDS_FRESH_S,
  endpoints: (builder) => ({
    getHomeCards: builder.query<HomeCardsResponse, HomeCardsMember>({
      query: ({ orgId }) => ({ url: '/home/cards', headers: expectOrg(orgId) }),
      providesTags: ['HomeCards'],
    }),
    dismissHomeCard: builder.mutation<void, HomeCardsMember & { id: string }>({
      query: ({ id, orgId }) => ({
        url: `/home/cards/${encodeURIComponent(id)}/dismiss`,
        method: 'POST',
        headers: expectOrg(orgId),
      }),
      invalidatesTags: ['HomeCards'],
    }),
    markHomeCardClicked: builder.mutation<
      void,
      HomeCardsMember & { id: string }
    >({
      query: ({ id, orgId }) => ({
        url: `/home/cards/${encodeURIComponent(id)}/clicked`,
        method: 'POST',
        headers: expectOrg(orgId),
      }),
      invalidatesTags: ['HomeCards'],
    }),
  }),
});

export const {
  useGetHomeCardsQuery,
  useDismissHomeCardMutation,
  useMarkHomeCardClickedMutation,
} = homeCardsApi;
